import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, IconButton, Link, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Modal } from "Components/_Shared/BlModal";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as ArrowDownIcon } from "Components/_Shared/Icons/Assets/arrow-down.svg";
import { ReactComponent as CalculatorIcon } from "Components/_Shared/Icons/Assets/calculator.svg";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { type StateAidCalculationFormType, stateAidCalculationFormSchema } from "Forms/Schemas/Insurance/stateAidCalculationFormSchema";
import { getGetSocialSupportQueryKey, useUpdateSocialSupportCalculationParameters } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

type Props = {
  readonly analysisId: string;
  readonly versionId: string;
  readonly clientId: string;
  readonly readOnly?: boolean;
};

const StyledWrapper = styled(Box)`
  width: ${(props) => props.theme.spacing(200)};
  border-radius: ${(props) => props.theme.spacing(Radius.XXXL)};
  padding: ${(props) => props.theme.spacing(Spacing.XXL)};
  background: ${(props) => props.theme.colors.white};
`;

export const StateAidCalculationModal: FC<Props> = ({ readOnly, analysisId, versionId, clientId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useResource();

  const [open, setOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StateAidCalculationFormType>({
    resolver: yupResolver(stateAidCalculationFormSchema(t)),
  });

  const { mutateAsync: updateParameters } = useUpdateSocialSupportCalculationParameters({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetSocialSupportQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  const onClose = () => {
    reset();
    setOpen(false);
  };

  const onCalculate = async (data: StateAidCalculationFormType) => {
    try {
      await updateParameters({
        analysisId,
        versionId,
        clientGuid: clientId,
        data: {
          assessmentBase: {
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
            value: data.assessmentBase,
          },
          basicAmount: {
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
            value: data.basicAmount,
          },
          percentageAmount: {
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
            value: data.percentageAmount,
          },
        },
      });
    } catch {
      toast.error(t(Resources.Common.Error));
    } finally {
      onClose();
    }
  };

  return (
    <>
      <Button
        variant="text"
        disabled={readOnly}
        colorCustom={theme.colors.brandPrimary}
        sx={{ width: "fit-content" }}
        startAdornment={<CalculatorIcon />}
        endAdornment={<ArrowDownIcon width={18} height={18} />}
        onClick={() => setOpen(true)}
      >
        {t(Resources.AnalysisDetail.Insurance.CalculateStateAidIndividually)}
      </Button>
      <Modal open={open} onClose={onClose}>
        <StyledWrapper sx={{ boxShadow: 3 }}>
          <Stack spacing={Spacing.XXL}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h2">{t(Resources.AnalysisDetail.Insurance.StateAidCalculation)}</Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box>
              <Typography display="inline" variant="body1">
                {t(Resources.AnalysisDetail.Insurance.StateAidCalculationInfo1)}
              </Typography>
              <Typography display="inline" fontWeight={400}>
                <Link href="https://eportal.cssz.cz/web/portal/informativni-duchodova-aplikace" target="_blank" rel="noopener noreferrer">
                  {t(Resources.AnalysisDetail.Insurance.StateAidCalculationLink)}
                </Link>
              </Typography>
              <Typography display="inline" variant="body1">
                {t(Resources.AnalysisDetail.Insurance.StateAidCalculationInfo2)}
              </Typography>
            </Box>
            <Grid container spacing={Spacing.XL} display="flex">
              <Grid size={4}>
                <BlFormInput
                  name="basicAmount"
                  label={t(Resources.AnalysisDetail.Insurance.BasicAmount)}
                  control={control}
                  errors={errors}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  name="assessmentBase"
                  label={t(Resources.AnalysisDetail.Insurance.AssesmentBase)}
                  control={control}
                  errors={errors}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  name="percentageAmount"
                  label={t(Resources.AnalysisDetail.Insurance.PercentageAmount)}
                  control={control}
                  errors={errors}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end">
              <Box mr={Spacing.XL}>
                <Button color="secondary" onClick={onClose}>
                  {t(Resources.Common.Close)}
                </Button>
              </Box>
              <Button
                disabled={readOnly}
                onClick={() => {
                  handleSubmit(onCalculate)();
                }}
              >
                {t(Resources.AnalysisDetail.Insurance.CalculateIndividually)}
              </Button>
            </Box>
          </Stack>
        </StyledWrapper>
      </Modal>
    </>
  );
};
